import React, { useEffect, useState, useContext, useMemo } from 'react'
import styled, { css } from 'styled-components'

import { WidgetContext } from 'live/context/widget'
import { DashboardWidgetContext } from 'live/context/dashboard-widget'
import { ChannelsAxisLabels, UnitSpan } from 'plugin-assets/shared/widget/components/widget-axis'

import TitleWithBadge from './TitleWithBadge'
import store from '../../store'
import { open, updateWidgetsWithAlarms, updateMutedWidgets } from '../../actions'
import { getHighestSeverity } from '../../utils'
import { HIGHEST_SEVERITY, SECOND_HIGHEST_SEVERITY, UPDATE_MUTED } from '../../constants'

const StyledAxis = styled.div`
    cursor: pointer;

    li {
        box-shadow: ${({ severity }) =>
            severity === HIGHEST_SEVERITY
                ? 'inset 0px 0px 16px -5px red'
                : severity === SECOND_HIGHEST_SEVERITY
                ? 'inset 0px 0px 16px -5px rgb(248, 147, 0)'
                : 'none'};

        &:hover {
            background: #ccc;
        }

        > .widget-axis__values {
            padding: ${({ isVertical }) => (isVertical ? '1px 15px' : '15px 1px')};
        }
    }
    ${({ isVertical }) => (isVertical ? 'width: 100%;' : 'height: 100%;')}
`

export function AlarmAxis(props) {
    const uom = props.uom || props.axis.__uom

    const axis = {
        ...props.axis,
        componentLabel: props.axis.componentLabel || (
            <>
                {props.label} {uom ? <UnitSpan>{`(${uom})`}</UnitSpan> : ''}
            </>
        )
    }

    const [storeState, setStoreState] = useState(store.getState())

    const { widget, setCss } = useContext(WidgetContext)

    const channel = axis.__mnemonic || axis.fields[0]
    const channelAlarms = useMemo(
        () => (storeState.dashboardReducer.batch || []).filter(alarm => alarm.ref.mnemonic === channel),
        [channel, storeState.dashboardReducer.batch]
    )

    const alarmSeverity = getHighestSeverity(channelAlarms)

    const { setTitle } = useContext(DashboardWidgetContext)

    useEffect(() => {
        const d = store.subscribe(() => {
            setStoreState(store.getState())
        })

        return d
    }, [])

    useEffect(() => {
        if (alarmSeverity === SECOND_HIGHEST_SEVERITY || alarmSeverity === HIGHEST_SEVERITY) {
            if (widget) {
                store.dispatch(updateWidgetsWithAlarms(widget.id, channel, channelAlarms))
            }
        } else if (!alarmSeverity && widget) {
            store.dispatch(updateWidgetsWithAlarms(widget.id, channel, channelAlarms, true))
        }
    }, [storeState.dashboardReducer.batch, channel, widget, alarmSeverity, channelAlarms])

    useEffect(() => {
        const widgetsWithAlarms = storeState.dashboardReducer.widgetsWithAlarms
        const widgetAlarms = Object.keys(widgetsWithAlarms[widget.id] || {}).reduce(
            (alarms, channelName) => [...alarms, ...((widgetsWithAlarms[widget.id] || {})[channelName] || [])],
            []
        )

        const widgetSeverity = getHighestSeverity(widgetAlarms)

        if (widgetSeverity === SECOND_HIGHEST_SEVERITY || widgetSeverity === HIGHEST_SEVERITY) {
            setCss(css`
                .widget-axis {
                    background-color: inherit;

                    > div {
                        background: transparent;
                    }

                    ul > .header-item {
                        padding: 0;
                    }
                }

                &&& {
                    border: 1px solid ${widgetSeverity === HIGHEST_SEVERITY ? '#D9534F' : '#F8A000'};
                    background-color: ${widgetSeverity === HIGHEST_SEVERITY
                        ? 'rgba(255, 80, 0, 0.3)'
                        : 'rgba(255, 150, 0, 0.3)'};
                    transition: opacity 0.25s ease, background-color 1s ease;
                }
            `)
        } else if (!widgetSeverity && widget) {
            const channelsLength = Object.keys(widgetsWithAlarms[widget.id] || {}).length

            if (!channelsLength) {
                setCss(css`
                    .widget-axis ul > .header-item {
                        padding: 0;
                    }
                `)
            }
        }

        if (widget) {
            const widgetAlarmsNumber = Object.keys(widgetsWithAlarms[widget.id] || {}).reduce(
                (sum, channel) => sum + ((widgetsWithAlarms[widget.id] || {})[channel] || []).length,
                0
            )

            const soundStatus = widgetAlarms.map(alarm => {
                const id = alarm.latestTrigger?.alarm?.ref?.ruleId || alarm.ref.ruleId

                return storeState.editorReducer.soundStatus[id]
            })
            const noSound = soundStatus.every(status => !status)

            if (widgetAlarmsNumber) {
                setTitle(
                    <TitleWithBadge
                        nAlarms={widgetAlarmsNumber}
                        severity={widgetSeverity}
                        noSound={noSound}
                        widget={widget}
                        mutedWidgets={storeState.dashboardReducer.mutedWidgets}
                        updateMutedWidgets={(dashboardId, widgetId, muted) => {
                            store.dispatch(updateMutedWidgets(dashboardId, widgetId, muted))
                            storeState.editorReducer.broadcastChannel.postMessage({
                                type: UPDATE_MUTED,
                                dashboardId
                            })
                        }}
                    />
                )
            } else {
                setTitle(null)
            }
        }
    }, [
        alarmSeverity,
        storeState.dashboardReducer.widgetsWithAlarms,
        storeState.dashboardReducer.mutedWidgets,
        storeState.editorReducer.broadcastChannel,
        storeState.editorReducer.soundStatus,
        widget,
        setCss,
        setTitle
    ])

    function handleClick() {
        // pipeless widgets have __mnemonic and the description in fields,
        // pipes widgets do not have __mnemonic and have the mnemonic in fields
        const assetId = storeState.editorReducer.assetId
        const channels = Object.keys(storeState.editorReducer.curves)

        if ((assetId || assetId === 0) && channels.indexOf(channel) !== -1) {
            store.dispatch(open(channel))
        }
    }

    return (
        <StyledAxis {...props} severity={alarmSeverity} onClick={handleClick}>
            <ChannelsAxisLabels {...props} lastEventMap={{}} warnings={[]} axis={axis} />
        </StyledAxis>
    )
}
